import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Subject, Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, AspectViewModel, AspectService, Int32StringTuple, AspectGroupService, AspectGroupViewModel } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-aspect-details',
    templateUrl: './aspect-details.component.html',
    styleUrls: ['./aspect-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AspectDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate  {

    @ViewChild('form', { static: true }) form: NgForm;
    @ViewChild('app-aspect-text-list', { static: true })

    aspect: AspectViewModel;
    aspectGroup: AspectGroupViewModel = null;
    users$: Observable<Array<StringStringTuple>>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;
    pageTitle: string = "Aspektdetaljer";

    id: number;
    aspectGroupId: number;
    aspectForm: UntypedFormGroup;
    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private aspectGroupService: AspectGroupService,
        private aspectService: AspectService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('aspectId'));
        this.aspectGroupId = Number.parseInt(this.route.snapshot.paramMap.get('aspectGroupId'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();

        if (this.id) {
            this.aspectService.getAspectById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {
            this.aspectGroupService.getAspectGroupById(this.aspectGroupId).subscribe(ag => this.aspectGroupLoaded(ag),
                error => this.alertService.showStickyMessage(error));

            this.isNew = true;
            this.aspect = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    ngOnChanges(): void {
        if (this.aspect) {
            this.isNew = false;
        }
        else {
            this.isNew = true;
            this.aspect = this.initializeEmptyObject();
        }

        this.resetForm();
    }

    aspectGroupLoaded(aspectGroupViewModel: AspectGroupViewModel): void {
        this.aspectGroup = aspectGroupViewModel;
    }

    dataLoaded(aspect: AspectViewModel) {
        this.aspect = aspect;
        this.aspectGroupId = aspect.aspectGroupId;

        this.aspectGroupService.getAspectGroupById(this.aspectGroupId).subscribe(ag => this.aspectGroupLoaded(ag),
            error => this.alertService.showStickyMessage(error));

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.aspectForm.controls[name];
    }


    private buildForm() {
        this.aspectForm = this.formBuilder.group({
            aspectId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            enabled: '',
            description: '',
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.aspect) {
            this.isNew = true;
            this.aspect = this.initializeEmptyObject();
        }

        this.aspectForm.reset({
            aspectId: this.aspect.aspectId || '',
            name: this.aspect.name || '',
            enabled: this.aspect.enabled || '',
            description: this.aspect.description || '',
            createdBy: this.aspect.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.aspect.createdDate, true) || '',
            updatedBy: this.aspect.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.aspect.updatedDate, true) || '',
        });

        if (this.aspect.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.aspect.createdBy).pipe(map(t => t.item2));
        }
        if (this.aspect.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.aspect.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): AspectViewModel {
        var result = <AspectViewModel>{};
        result.enabled = true;
        result.aspectGroupId = this.aspectGroupId;

        return result;
    }

    private prepareSave(): AspectViewModel {
        const formModel = this.aspectForm.value;

        return {
            aspectId: this.isNew ? null : formModel.aspectId,
            aspectGroupId: this.aspectGroupId,
            name: formModel.name,
            enabled: formModel.enabled ? true : false,
            description: formModel.description,
        };
    }

    public save() {
        if (!this.aspectForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const aspect = this.prepareSave();

        if (this.isNew) {
            this.aspectService.createAspect(aspect).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.aspectService.updateAspect(aspect.aspectId, aspect).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.aspectForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }

    private saveCompleted(aspect: AspectViewModel) {
        this.aspect = aspect;
        this.id = aspect.aspectId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), aspect.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    getPageTitle(): string {
        if (this.isNew) {
            return "Ny aspekt för aspektgrupp: " + (this.aspectGroup != null ? this.aspectGroup.name : "");
        } else {
            return "Aspekt " + (this.aspect == null ? "" : this.aspect.name) + " för aspektgrupp: " + (this.aspectGroup != null ? this.aspectGroup.name : "");
        }
    }

}
